import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
  Input,
  SectionCard,
  FormSection,
  RequiredMark,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { NoticeSpecItemsEditor } from './NoticeSpecItemsEditor';
import type { NoticeSpecTemplate, NoticeSpecTemplatePayload } from '../hooks/useNoticeSpecTemplates';

/**
 * QA-80 — 상품정보고시 템플릿 생성/수정 모달. 템플릿명(필수) + 고시 항목(라벨→값) 행 편집.
 * 저장 시 payload({ name, items, translations }) 를 상위 mutation 으로 전달한다.
 *
 * Task#6 — locale 탭(한국어 정본 + en/ja/zh 번역): 한국어 탭이 top-level items(정본),
 * 번역 탭은 translations.{locale} 을 편집한다. 번역이 비어 있는 locale 은 상품 폼에서
 * 한국어 항목으로 폴백 적용된다. 저장 시 3개 번역 locale 키를 항상 명시 전송(빈맵=삭제,
 * BE NoticeSpecTemplateRequest 결정적 전체 교체 시맨틱).
 */
export interface NoticeSpecTemplateFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  editTarget?: NoticeSpecTemplate | null;
  isPending: boolean;
  onSubmit: (payload: NoticeSpecTemplatePayload) => void;
}

/** 번역 locale 목록 — ko-KR 은 top-level items 가 정본이라 제외. */
export const NOTICE_TEMPLATE_TRANSLATION_LOCALES = ['en-US', 'ja-JP', 'zh-CN'] as const;
export type NoticeTemplateTranslationLocale = (typeof NOTICE_TEMPLATE_TRANSLATION_LOCALES)[number];

const LOCALE_LABELS: Record<NoticeTemplateTranslationLocale, string> = {
  'en-US': 'English',
  'ja-JP': '日本語',
  'zh-CN': '中文',
};

type TranslationsState = Record<NoticeTemplateTranslationLocale, Record<string, string>>;

const emptyTranslations = (): TranslationsState => ({
  'en-US': {},
  'ja-JP': {},
  'zh-CN': {},
});

const seedTranslations = (target: NoticeSpecTemplate | null | undefined): TranslationsState => {
  const next = emptyTranslations();
  if (!target?.translations) return next;
  for (const loc of NOTICE_TEMPLATE_TRANSLATION_LOCALES) {
    const entry = target.translations[loc];
    if (entry && Object.keys(entry).length > 0) next[loc] = { ...entry };
  }
  return next;
};

export const NoticeSpecTemplateFormModal = ({
  open,
  onOpenChange,
  mode,
  editTarget,
  isPending,
  onSubmit,
}: NoticeSpecTemplateFormModalProps) => {
  const { t } = useTranslation('products');
  const [name, setName] = useState('');
  const [items, setItems] = useState<Record<string, string>>({});
  const [translations, setTranslations] = useState<TranslationsState>(emptyTranslations());

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && editTarget) {
      setName(editTarget.name);
      setItems({ ...editTarget.items });
      setTranslations(seedTranslations(editTarget));
    } else {
      setName('');
      setItems({});
      setTranslations(emptyTranslations());
    }
  }, [open, mode, editTarget]);

  const setLocaleItems = (loc: NoticeTemplateTranslationLocale, next: Record<string, string>) =>
    setTranslations((prev) => ({ ...prev, [loc]: next }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    // 3개 locale 키를 항상 명시 전송 — 비운 locale 은 빈맵(삭제), 채운 locale 은 전체 교체.
    onSubmit({ name: name.trim(), items, translations: { ...translations } });
  };

  const titleText =
    mode === 'create'
      ? t('products.noticeSpecTemplates.createTitle', { defaultValue: '고시 템플릿 등록' })
      : t('products.noticeSpecTemplates.editTitle', { defaultValue: '고시 템플릿 수정' });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* 고시 항목은 라벨+값 2입력/행 편집이라 폭이 필요하다. 모달을 넓히고(3xl) 고시 항목 섹션은
          좌측 라벨 없이 풀폭으로 둬(아래) 값 입력칸이 충분한 너비를 갖게 한다. */}
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{titleText}</DialogTitle>
          <DialogDescription>
            {t('products.noticeSpecTemplates.formDesc', {
              defaultValue: '품목별 표준 고시 항목을 템플릿으로 저장해 상품 등록 시 선택·적용합니다.',
            })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <SectionCard>
            <div className="flex flex-col gap-4">
              <FormSection
                density="compact"
                label={t('products.noticeSpecTemplates.name', { defaultValue: '템플릿명' })}
                required
              >
                <Input
                  id="notice-tpl-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoFocus
                  maxLength={100}
                  placeholder={t('products.noticeSpecTemplates.namePlaceholder', { defaultValue: '예: MD 상품 고시' })}
                  style={{ height: 'var(--density-compact-form-h)' }}
                />
              </FormSection>
              {/* 고시 항목은 좌측 라벨 FormSection 대신 라벨을 위로 올린 풀폭 레이아웃 —
                  라벨+값 다중 입력 행이 모달 전체 폭을 쓰게 해 값 입력칸 붕괴를 방지한다. */}
              <div className="flex flex-col gap-2">
                <span
                  className="text-[13px] font-medium"
                  style={{ color: 'var(--content-strong)' }}
                >
                  {t('products.noticeSpecTemplates.items', { defaultValue: '고시 항목' })}
                </span>
                {/* Task#6 — locale 탭: 한국어(정본) + en/ja/zh 번역. */}
                <Tabs defaultValue="ko-KR" className="w-full">
                  <TabsList className="mb-2">
                    <TabsTrigger value="ko-KR">
                      한국어
                      <RequiredMark />
                    </TabsTrigger>
                    {NOTICE_TEMPLATE_TRANSLATION_LOCALES.map((loc) => (
                      <TabsTrigger key={loc} value={loc}>
                        {LOCALE_LABELS[loc]}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  <TabsContent value="ko-KR">
                    <NoticeSpecItemsEditor value={items} disabled={isPending} onChange={setItems} />
                  </TabsContent>
                  {NOTICE_TEMPLATE_TRANSLATION_LOCALES.map((loc) => (
                    <TabsContent key={loc} value={loc} className="flex flex-col gap-2">
                      <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                        {t('products.noticeSpecTemplates.localeFallbackHint', {
                          defaultValue: '비워 두면 상품 폼에서 이 언어에 한국어(정본) 항목이 적용됩니다.',
                        })}
                      </p>
                      <NoticeSpecItemsEditor
                        value={translations[loc]}
                        disabled={isPending}
                        onChange={(next) => setLocaleItems(loc, next)}
                      />
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            </div>
          </SectionCard>
          <DialogFooter className="mt-4">
            <DialogClose asChild>
              <Button type="button" variant="ghost" size="sm">
                {t('products.actions.cancel', { defaultValue: '취소' })}
              </Button>
            </DialogClose>
            <Button type="submit" size="sm" disabled={isPending || !name.trim()}>
              {t('products.actions.save', { defaultValue: '저장' })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
